/**
 * constants.js - Constantes y utilidades compartidas de la aplicación DJ Management
 * 
 * Este archivo centraliza:
 * - Géneros musicales predefinidos
 * - Momentos de eventos (bodas, etc.)
 * - Funciones de utilidad (normalización, búsqueda, formato)
 */

// ============================================
// GÉNEROS MUSICALES
// ============================================

export const INITIAL_GENRES = [
  // Décadas
  "60s-70s", "80s", "90s", "2000s",
  // Latino  
  "Bachata", "Merengue", "Reggaeton", "Reggaeton clásico",
  "Electro latino", "Rumbas", "Cumbia", "Clasicos latinos",
  // Pop
  "Pop español", "Pop clásico español",
  // Rock
  "Rock español", "Rock clásico español", "Rock clásico internacional",
  // Electrónica/Dance
  "Dance clásico", "House", "Remember",
  // Urbano
  "Rap",
  // Especiales
  "Ambiente banquete", "Canción del verano", "Pachanga", "Humor", "Navidad",
  // Otros géneros
  "Baladas", "BOLERO", "Blues", "Indie español", "Soul RB", 
  "Valses", "Remixes", "Euskera", "catalan"
];

// ============================================
// MOMENTOS DE EVENTOS
// ============================================

export const MOMENTOS_BODA = [
  "Ceremonia", "Cóctel", "Primera Baile", "Baile General", "Cierre",
  "Hora Loca", "Tarta", "Entrada Banquete", "Verbena"
];

// Momentos expandidos para otros tipos de eventos
export const MOMENTOS_EVENTO = {
  boda: MOMENTOS_BODA,
  comunion: ["Ceremonia", "Cóctel", "Aperitivo", "Comida", "Baile", "Cierre"],
  corporativo: ["Recepción", "Presentación", "Networking", "Cena", "Fiesta"],
  cumpleanos: ["Bienvenida", "Aperitivo", "Comida", "Tarta", "Baile", "Cierre"],
  fiesta: ["Apertura", "Calentamiento", "Peak Time", "Cierre"]
};

// ============================================
// ESTADOS DE EVENTOS
// ============================================

export const ESTADOS_EVENTO = {
  pendiente: { label: 'Pendiente', color: 'yellow' },
  confirmado: { label: 'Confirmado', color: 'blue' },
  completado: { label: 'Completado', color: 'green' },
  cancelado: { label: 'Cancelado', color: 'red' }
};

// ============================================
// FUNCIONES DE UTILIDAD
// ============================================

/**
 * Normaliza texto: minúsculas, sin tildes, sin espacios extra
 * @param {string} text - Texto a normalizar
 * @returns {string} Texto normalizado
 */
export const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

/**
 * Búsqueda flexible: permite palabras no contiguas
 * Ej: "pido luna" encuentra "Yo no te pido la luna"
 * @param {string} text - Texto donde buscar
 * @param {string} searchTerms - Términos de búsqueda
 * @returns {boolean} True si todas las palabras están presentes
 */
export const flexibleSearch = (text, searchTerms) => {
  if (!searchTerms || !text) return true;
  const normalizedText = normalizeText(text);
  const words = normalizeText(searchTerms).split(/\s+/).filter(w => w.length > 0);
  
  // Todas las palabras deben estar presentes en el texto
  return words.every(word => normalizedText.includes(word));
};

/**
 * Obtiene el caché/precio efectivo de un evento
 * Prioridad: cache_acordado > presupuesto_total > suma de servicios
 * @param {object} event - Objeto del evento
 * @returns {number} Precio efectivo
 */
export const getEffectiveCache = (event) => {
  if (!event) return 0;
  
  // 1. Si tiene cache_acordado, usarlo
  if (event.cache_acordado && parseFloat(event.cache_acordado) > 0) {
    return parseFloat(event.cache_acordado);
  }
  
  // 2. Si tiene presupuesto_total, usarlo
  if (event.presupuesto_total && parseFloat(event.presupuesto_total) > 0) {
    return parseFloat(event.presupuesto_total);
  }
  
  // 3. Calcular desde servicios contratados
  if (event.servicios_contratados && Array.isArray(event.servicios_contratados)) {
    const total = event.servicios_contratados
      .filter(s => s.contratado)
      .reduce((sum, s) => {
        const precio = s.precio_personalizado ?? s.precio_final ?? s.precio_base ?? 0;
        const cantidad = s.cantidad || 1;
        return sum + (precio * cantidad);
      }, 0);
    if (total > 0) return total;
  }
  
  return 0;
};

/**
 * Formatea un número como moneda (EUR)
 * @param {number} amount - Cantidad a formatear
 * @returns {string} Cantidad formateada
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
};

/**
 * Formatea una fecha en español
 * @param {string|Date} date - Fecha a formatear
 * @param {object} options - Opciones de formato
 * @returns {string} Fecha formateada
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '-';
  const defaultOptions = { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric',
    ...options
  };
  return new Date(date).toLocaleDateString('es-ES', defaultOptions);
};

/**
 * Formatea una fecha corta (DD/MM/YYYY)
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
export const formatDateShort = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-ES');
};

// ============================================
// CONFIGURACIÓN DE COLORES POR TIPO DE EVENTO
// ============================================

export const EVENT_TYPE_COLORS = {
  boda: { bg: 'bg-pink-600', text: 'text-pink-300', border: 'border-pink-600' },
  comunion: { bg: 'bg-blue-600', text: 'text-blue-300', border: 'border-blue-600' },
  corporativo: { bg: 'bg-slate-600', text: 'text-slate-300', border: 'border-slate-600' },
  cumpleanos: { bg: 'bg-purple-600', text: 'text-purple-300', border: 'border-purple-600' },
  fiesta: { bg: 'bg-amber-600', text: 'text-amber-300', border: 'border-amber-600' },
  default: { bg: 'bg-cyan-600', text: 'text-cyan-300', border: 'border-cyan-600' }
};

/**
 * Obtiene los colores para un tipo de evento
 * @param {string} eventType - Tipo de evento
 * @returns {object} Objeto con clases de color
 */
export const getEventTypeColors = (eventType) => {
  const type = eventType?.toLowerCase() || 'default';
  return EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.default;
};
